import { useEffect, useRef, useState, FormEvent } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { AutoTextarea } from '../components/AutoTextarea';
import { DictateButton } from '../components/DictateButton';
import { SignaturePad, type SignaturePadHandle } from '../components/SignaturePad';
import { useUserOrg } from '../lib/org';
import { getSite, createDocument, getDocument, type Site } from '../lib/db';
import { uploadDocument } from '../lib/storage';
import { supabase } from '../lib/supabase';
import { DOC_TEMPLATES } from '../data/docTemplates';
import { generateDocumentPdf } from '../lib/pdf';
import { LoadingIndicator } from '../components/LoadingIndicator';

async function blobToDataUrl(blob: Blob): Promise<string | null> {
  return new Promise<string | null>((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(blob);
  });
}

/**
 * Generic tick-row used by both tick-list FIELDS and tick-list SECTIONS.
 * Renders a check, the item label, and an × to remove it from the list.
 */
function TickRow({
  label, checked, onToggle, onRemove,
}: { label: string; checked: boolean; onToggle: () => void; onRemove: () => void }) {
  return (
    <label className="check-row">
      <input type="checkbox" checked={checked} onChange={onToggle} />
      <span style={{ flex: 1 }}>{label}</span>
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); onRemove(); }}
        aria-label={`Remove ${label}`}
        style={{
          background: 'transparent', border: 'none',
          color: 'var(--ink-soft)', fontSize: 18, padding: '0 4px',
          cursor: 'pointer',
        }}
      >×</button>
    </label>
  );
}

/**
 * Custom-item add input shared by both field and section tick lists.
 */
function TickAddInput({
  value, onChange, onAdd, placeholder,
}: { value: string; onChange: (v: string) => void; onAdd: () => void; placeholder: string }) {
  return (
    <div className="field" style={{ marginTop: 10, marginBottom: 0 }}>
      <div style={{ display: 'flex', gap: 8 }}>
        <input
          type="text"
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); onAdd(); }
          }}
          style={{ flex: 1 }}
        />
        <button
          type="button"
          className="btn btn-ghost"
          onClick={onAdd}
          disabled={!value.trim()}
          style={{ flex: '0 0 auto', padding: '0 16px' }}
        >+ Add</button>
      </div>
    </div>
  );
}

interface TickListFieldProps {
  fieldKey: string;
  items: string[];
  checks: boolean[];
  customValue: string;
  onToggle: (key: string, idx: number) => void;
  onRemove: (key: string, idx: number) => void;
  onAdd: (key: string) => void;
  onCustomChange: (value: string) => void;
}

function TickListField({
  fieldKey, items, checks, customValue,
  onToggle, onRemove, onAdd, onCustomChange,
}: TickListFieldProps) {
  return (
    <>
      {items.length > 0 && (
        <div className="check-list" style={{ marginTop: 4 }}>
          {items.map((item, i) => (
            <TickRow
              key={`${i}-${item}`}
              label={item}
              checked={checks[i] ?? false}
              onToggle={() => onToggle(fieldKey, i)}
              onRemove={() => onRemove(fieldKey, i)}
            />
          ))}
        </div>
      )}
      <TickAddInput
        value={customValue}
        onChange={onCustomChange}
        onAdd={() => onAdd(fieldKey)}
        placeholder="Add another option…"
      />
    </>
  );
}

interface SectionTickListProps {
  sectionIdx: number;
  items: string[];
  checks: boolean[];
  customValue: string;
  onToggle: (sectionIdx: number, itemIdx: number) => void;
  onRemove: (sectionIdx: number, itemIdx: number) => void;
  onAdd: (sectionIdx: number) => void;
  onCustomChange: (value: string) => void;
}

function SectionTickList({
  sectionIdx, items, checks, customValue,
  onToggle, onRemove, onAdd, onCustomChange,
}: SectionTickListProps) {
  return (
    <>
      {items.length === 0 ? (
        <p className="muted" style={{ fontSize: 13, margin: '4px 0 8px' }}>
          No items yet — add one below.
        </p>
      ) : (
        <div className="check-list">
          {items.map((item, i) => (
            <TickRow
              key={`${i}-${item}`}
              label={item}
              checked={checks[i] ?? false}
              onToggle={() => onToggle(sectionIdx, i)}
              onRemove={() => onRemove(sectionIdx, i)}
            />
          ))}
        </div>
      )}
      <TickAddInput
        value={customValue}
        onChange={onCustomChange}
        onAdd={() => onAdd(sectionIdx)}
        placeholder="Add another clause…"
      />
    </>
  );
}

export function DocumentFillForm() {
  const { siteId, templateId } = useParams<{ siteId?: string; templateId: string }>();
  const [searchParams] = useSearchParams();
  const supersedeId = searchParams.get('supersede');
  const nav = useNavigate();
  const { org } = useUserOrg();
  const docIdRef = useRef<string>(crypto.randomUUID());
  const sigRef = useRef<SignaturePadHandle>(null);

  const template = DOC_TEMPLATES.find((t) => t.id === templateId);
  const isSiteScope = !!siteId;

  const [site, setSite] = useState<Site | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [sectionBodies, setSectionBodies] = useState<string[]>([]);
  // Tick-first sections: per-section state for which bullets the user
  // has selected and any custom items they've added beyond the template's
  // default list. Indexed by section index in template.sections.
  const [sectionItems, setSectionItems] = useState<Record<number, string[]>>({});
  const [sectionItemChecks, setSectionItemChecks] = useState<Record<number, boolean[]>>({});
  const [sectionItemCustom, setSectionItemCustom] = useState<Record<number, string>>({});
  // Tick-list fields: per-field-key custom items + check state.
  const [fieldTickItems, setFieldTickItems] = useState<Record<string, string[]>>({});
  const [fieldTickChecks, setFieldTickChecks] = useState<Record<string, boolean[]>>({});
  const [fieldTickCustom, setFieldTickCustom] = useState<Record<string, string>>({});
  const [signatoryName, setSignatoryName] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    setLoading(true);
    setError(null);
    (async () => {
      try {
        // Load the site up-front so we can seed template fields with its
        // name / address without waiting for setState to flush. Without this,
        // opening PCI / PQQ / CPP from Site Setup prompted the user to
        // re-type the site name every time.
        const loadedSite = siteId ? await getSite(siteId) : null;
        if (loadedSite) setSite(loadedSite);
        if (template) {
          const initFields: Record<string, string> = {};
          // Seed tick-list field state alongside scalar field state.
          const initTickItems: Record<string, string[]> = {};
          const initTickChecks: Record<string, boolean[]> = {};
          const initTickCustom: Record<string, string> = {};
          for (const f of template.fields) {
            // Auto-populate common field keys from site + org so users
            // don't retype the same info on every document. Kept
            // conservative on purpose — only fields where we're confident
            // about the source. Users can always overwrite in-form.
            let seed = '';
            if (loadedSite) {
              if (f.key === 'project_name' || f.key === 'site_name' || f.key === 'job_name') {
                seed = loadedSite.name;
              }
              if (f.key === 'site_address' || f.key === 'project_address') {
                // Site.address may be undefined for older sites — fall through to '' in that case.
                seed = (loadedSite as { address?: string }).address ?? '';
              }
            }
            if (org?.org_name) {
              if (f.key === 'pc_name' || f.key === 'principal_contractor') {
                seed = org.org_name;
              }
            }
            initFields[f.key] = seed;
            if (f.type === 'tickList') {
              const opts = (f.tickOptions ?? []).slice();
              initTickItems[f.key] = opts;
              initTickChecks[f.key] = opts.map(() => true);
              initTickCustom[f.key] = '';
            }
          }
          setFieldValues(initFields);
          setFieldTickItems(initTickItems);
          setFieldTickChecks(initTickChecks);
          setFieldTickCustom(initTickCustom);
          setSectionBodies(template.sections.map((s) => s.body));
          // Seed tick-first sections from each section's items[] list.
          const itemsByIdx: Record<number, string[]> = {};
          const checksByIdx: Record<number, boolean[]> = {};
          const customByIdx: Record<number, string> = {};
          template.sections.forEach((s, i) => {
            if (s.items && s.items.length > 0) {
              itemsByIdx[i] = s.items.slice();
              checksByIdx[i] = s.items.map(() => true);
              customByIdx[i] = '';
            }
          });
          setSectionItems(itemsByIdx);
          setSectionItemChecks(checksByIdx);
          setSectionItemCustom(customByIdx);
        }
        // Pre-fill signatory from auth user
        const { data } = await supabase.auth.getUser();
        const name = (data.user?.user_metadata?.full_name as string | undefined) ?? '';
        if (name) setSignatoryName(name);

        // If superseding, copy values from previous version
        if (supersedeId) {
          const prev = await getDocument(supersedeId);
          if (prev?.notes) setNotes(prev.notes);
          if (prev?.expires_at) setExpiresAt(prev.expires_at.slice(0, 10));
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [siteId, templateId, supersedeId, template]);

  if (loading) return <AppShell title="Loading…"><LoadingIndicator /></AppShell>;
  if (!template) return (
    <AppShell title="Document">
      <div className="banner banner-error">Template not found.</div>
      <button type="button" className="btn btn-ghost" onClick={() => nav('/documents')} style={{ marginTop: 12 }}>
        Back to Documents
      </button>
    </AppShell>
  );

  function setField(key: string, value: string) {
    setFieldValues((p) => ({ ...p, [key]: value }));
  }
  function setSection(idx: number, val: string) {
    setSectionBodies((p) => p.map((b, i) => (i === idx ? val : b)));
  }

  // --- Tick-first helpers --------------------------------------------------
  // Sections-with-items: per-section tick handling. The ticked subset is
  // serialised at submit time into the section body for the PDF.
  function toggleSectionItem(sectionIdx: number, itemIdx: number) {
    setSectionItemChecks((prev) => {
      const cur = prev[sectionIdx] ?? [];
      const next = cur.slice();
      next[itemIdx] = !next[itemIdx];
      return { ...prev, [sectionIdx]: next };
    });
  }
  function removeSectionItem(sectionIdx: number, itemIdx: number) {
    setSectionItems((prev) => {
      const cur = prev[sectionIdx] ?? [];
      return { ...prev, [sectionIdx]: cur.filter((_, j) => j !== itemIdx) };
    });
    setSectionItemChecks((prev) => {
      const cur = prev[sectionIdx] ?? [];
      return { ...prev, [sectionIdx]: cur.filter((_, j) => j !== itemIdx) };
    });
  }
  function addSectionItem(sectionIdx: number) {
    const text = (sectionItemCustom[sectionIdx] ?? '').trim();
    if (!text) return;
    setSectionItems((prev) => ({
      ...prev,
      [sectionIdx]: [...(prev[sectionIdx] ?? []), text],
    }));
    setSectionItemChecks((prev) => ({
      ...prev,
      [sectionIdx]: [...(prev[sectionIdx] ?? []), true],
    }));
    setSectionItemCustom((prev) => ({ ...prev, [sectionIdx]: '' }));
  }

  // tickList fields: per-field tick handling. The joined string is mirrored
  // into fieldValues[key] so the existing PDF render path picks it up.
  function syncTickListValue(key: string, items: string[], checks: boolean[]) {
    const selected = items.filter((_, i) => checks[i]).join(' · ');
    setFieldValues((p) => ({ ...p, [key]: selected }));
  }
  function toggleFieldTick(key: string, idx: number) {
    setFieldTickChecks((prev) => {
      const cur = prev[key] ?? [];
      const next = cur.slice();
      next[idx] = !next[idx];
      syncTickListValue(key, fieldTickItems[key] ?? [], next);
      return { ...prev, [key]: next };
    });
  }
  function removeFieldTick(key: string, idx: number) {
    const curItems = (fieldTickItems[key] ?? []).filter((_, j) => j !== idx);
    const curChecks = (fieldTickChecks[key] ?? []).filter((_, j) => j !== idx);
    setFieldTickItems((p) => ({ ...p, [key]: curItems }));
    setFieldTickChecks((p) => ({ ...p, [key]: curChecks }));
    syncTickListValue(key, curItems, curChecks);
  }
  function addFieldTick(key: string) {
    const text = (fieldTickCustom[key] ?? '').trim();
    if (!text) return;
    const curItems = [...(fieldTickItems[key] ?? []), text];
    const curChecks = [...(fieldTickChecks[key] ?? []), true];
    setFieldTickItems((p) => ({ ...p, [key]: curItems }));
    setFieldTickChecks((p) => ({ ...p, [key]: curChecks }));
    setFieldTickCustom((p) => ({ ...p, [key]: '' }));
    syncTickListValue(key, curItems, curChecks);
  }

  // Serialise a tick-list section into the body text the PDF expects:
  // a newline-separated bulleted list of the ticked items.
  function sectionBodyFromTicks(sectionIdx: number): string {
    const items = sectionItems[sectionIdx] ?? [];
    const checks = sectionItemChecks[sectionIdx] ?? [];
    return items
      .filter((_, i) => checks[i])
      .map((t) => `• ${t}`)
      .join('\n');
  }

  function failWith(msg: string) {
    setError(msg);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!template) return;
    if (!org) {
      failWith('Organisation not loaded yet. Wait a moment and try again.');
      return;
    }
    if (template.signatureRequired && !signatoryName.trim()) {
      failWith('Signatory name is required.');
      return;
    }
    if (template.expiryRequired && !expiresAt) {
      failWith('Expiry date is required for this document.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const docId = docIdRef.current;
      const dateStr = new Date().toLocaleDateString('en-GB', {
        day: '2-digit', month: 'long', year: 'numeric',
      });

      let signatureDataUrl: string | null = null;
      if (template.signatureRequired && sigRef.current && !sigRef.current.isEmpty()) {
        signatureDataUrl = await blobToDataUrl(await sigRef.current.toBlob());
      }

      const renderInput = {
        orgName: org.org_name,
        orgLogoPath: org.logo_path,
        title: template.title,
        category: template.category,
        scope: template.scope,
        // Passed through so the house-style PDF renderer can pull
        // the doc-specific purpose paragraph and CDM subtitle from
        // HOUSE_STYLE_PURPOSES / HOUSE_STYLE_SUBTITLES in pdf.ts.
        templateId: template.id,
        siteName: site?.name ?? null,
        fields: template.fields.map((f) => ({ key: f.key, label: f.label, value: fieldValues[f.key] ?? '' })),
        // For tick-driven sections, derive the body from the ticked items.
        // For traditional editable sections, use the textarea value.
        sections: template.sections.map((s, i) => ({
          heading: s.heading,
          body: (s.items && s.items.length > 0)
            ? sectionBodyFromTicks(i)
            : (sectionBodies[i] ?? s.body),
        })),
        signatureDataUrl,
        signatoryName: signatoryName.trim(),
        date: dateStr,
        expiresAt: expiresAt || null,
        notes: notes.trim() || null,
      };

      const blob = await generateDocumentPdf(renderInput);

      const fileName = `${template.id}-${(site?.name ?? 'org').replace(/\s+/g, '-')}-${dateStr.replace(/\s+/g, '-')}.pdf`;
      const file = new File([blob], fileName, { type: 'application/pdf' });
      const path = await uploadDocument(org.org_id, docId, file);

      await createDocument({
        id: docId,
        orgId: org.org_id,
        siteId: isSiteScope ? siteId! : null,
        templateId: template.id,
        title: template.title,
        category: template.category,
        scope: template.scope,
        status: 'issued',
        filePath: path,
        fileName,
        mimeType: 'application/pdf',
        sizeBytes: file.size,
        expiresAt: expiresAt || null,
        notes: notes.trim() || null,
        supersedeId: supersedeId,
      });

      // Land on the document detail page with ?share=1 so the ShareModal
      // opens automatically — gives the user a chance to email the doc
      // straight after creating it without having to click around.
      nav(`/documents/${docId}?share=1`);
    } catch (e) {
      console.error('[DocumentFillForm] save failed:', e);
      failWith((e instanceof Error ? e.message : String(e)) + ' — check browser console for details.');
      setSubmitting(false);
    }
  }

  return (
    <AppShell title={template.title} back="/documents">
      {/* Intro card — the template title is already shown in the AppShell
          header above, so we don't repeat it here. Description + scope/
          category metadata still earn their place. */}
      <div className="card" style={{ marginBottom: 12 }}>
        <p style={{ margin: 0, fontSize: 13, color: 'var(--ink-soft)' }}>{template.description}</p>
        <p style={{ margin: '8px 0 0', fontSize: 12, color: 'var(--ink-soft)' }}>
          Scope: <strong>{template.scope === 'org' ? 'Organisation-wide' : `Site: ${site?.name ?? '—'}`}</strong>
          {' · '}Category: <strong>{template.category.toUpperCase()}</strong>
          {supersedeId && ' · creating new version (supersedes previous)'}
        </p>
      </div>

      {error && <div className="banner banner-error">{error}</div>}

      <form onSubmit={onSubmit}>
        {template.fields.length > 0 && (
          <>
            <h3 className="section-title">Document details</h3>
            {template.fields.map((f) => (
              <div key={f.key} className={`field${f.required ? ' needs-input' : ''}`}>
                <label htmlFor={`f-${f.key}`}>{f.label}{f.required ? ' *' : ''}</label>
                {f.type === 'textarea' ? (
                  <AutoTextarea
                    id={`f-${f.key}`}
                    minRows={3}
                    placeholder={f.placeholder}
                    value={fieldValues[f.key] ?? ''}
                    onChange={(e) => setField(f.key, e.target.value)}
                    required={f.required}
                  />
                ) : f.type === 'select' ? (
                  <select
                    id={`f-${f.key}`}
                    value={fieldValues[f.key] ?? ''}
                    onChange={(e) => setField(f.key, e.target.value)}
                    required={f.required}
                  >
                    <option value="">— Choose one —</option>
                    {(f.options ?? []).map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                ) : f.type === 'tickList' ? (
                  <TickListField
                    fieldKey={f.key}
                    items={fieldTickItems[f.key] ?? []}
                    checks={fieldTickChecks[f.key] ?? []}
                    customValue={fieldTickCustom[f.key] ?? ''}
                    onToggle={toggleFieldTick}
                    onRemove={removeFieldTick}
                    onAdd={addFieldTick}
                    onCustomChange={(v) => setFieldTickCustom((p) => ({ ...p, [f.key]: v }))}
                  />
                ) : (
                  <input
                    id={`f-${f.key}`}
                    type={f.type === 'date' ? 'date' : f.type === 'number' ? 'number' : 'text'}
                    placeholder={f.placeholder}
                    value={fieldValues[f.key] ?? ''}
                    onChange={(e) => setField(f.key, e.target.value)}
                    required={f.required}
                  />
                )}
                {f.type === 'select' && fieldValues[f.key] && (
                  <p className="muted" style={{ fontSize: 12, margin: '6px 0 0', fontStyle: 'italic' }}>
                    {(f.options ?? []).find((o) => o.value === fieldValues[f.key])?.hint}
                  </p>
                )}
                {f.hint && (
                  <p className="muted" style={{ fontSize: 12, margin: '4px 0 0' }}>
                    {f.hint.split(/(\bhttps?:\/\/[^\s]+)/g).map((part, i) =>
                      /^https?:\/\//.test(part) ? (
                        <a key={i} href={part} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--brand-deep)', textDecoration: 'underline' }}>{part}</a>
                      ) : <span key={i}>{part}</span>
                    )}
                  </p>
                )}
              </div>
            ))}
          </>
        )}

        {template.expiryRequired && (
          <>
            <h3 className="section-title" style={{ marginTop: 22 }}>Expiry</h3>
            <div className="field needs-input">
              <label htmlFor="d-expiry">Document expires *</label>
              <input
                id="d-expiry"
                type="date"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                required
              />
            </div>
          </>
        )}

        {template.sections.length > 0 && (
          <>
            <h3 className="section-title" style={{ marginTop: 22 }}>Document body — review and edit</h3>
            <div className="rams-callout">
              <strong>↓ Sections marked ✎ Edit required.</strong> Adapt content to fit your business / site before issuing.
            </div>
            {template.sections.map((s, i) => {
              const isTickSection = s.items && s.items.length > 0;
              const items = sectionItems[i] ?? [];
              const checks = sectionItemChecks[i] ?? [];
              const tickedCount = checks.filter(Boolean).length;
              return (
                <div key={i} className={`rams-section-edit${s.editable === false ? ' rams-section-locked' : ''}`}>
                  <div className="rams-section-head">
                    <span className="rams-section-label">{s.heading}</span>
                    {s.editable === false ? (
                      <span className="rams-section-lock">🔒 Locked</span>
                    ) : isTickSection ? (
                      <span className="rams-section-pen">
                        {tickedCount} of {items.length} ticked
                      </span>
                    ) : (
                      <span className="rams-section-pen">✎ Edit required</span>
                    )}
                  </div>
                  {s.hint && <p className="rams-section-hint">{s.hint}</p>}
                  {s.editable === false ? (
                    <div className="rams-section-body" style={{ whiteSpace: 'pre-wrap' }}>{s.body}</div>
                  ) : isTickSection ? (
                    <SectionTickList
                      sectionIdx={i}
                      items={items}
                      checks={checks}
                      customValue={sectionItemCustom[i] ?? ''}
                      onToggle={toggleSectionItem}
                      onRemove={removeSectionItem}
                      onAdd={addSectionItem}
                      onCustomChange={(v) => setSectionItemCustom((p) => ({ ...p, [i]: v }))}
                    />
                  ) : (
                    <div className="input-with-mic">
                      <AutoTextarea
                        minRows={3}
                        value={sectionBodies[i] ?? s.body}
                        onChange={(e) => setSection(i, e.target.value)}
                      />
                      <DictateButton
                        onTranscript={(t) => setSection(i, (sectionBodies[i] ?? s.body) + ' ' + t)}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}

        <div className="field" style={{ marginTop: 16 }}>
          <label htmlFor="d-notes">Notes (optional)</label>
          <div className="input-with-mic">
            <AutoTextarea
              id="d-notes"
              minRows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
            <DictateButton onTranscript={(t) => setNotes((p) => (p ? p + ' ' + t : t))} />
          </div>
        </div>

        {template.signatureRequired && (
          <>
            <h3 className="section-title" style={{ marginTop: 22 }}>Issued by</h3>
            <div className="field needs-input">
              <label htmlFor="d-by">Signatory name *</label>
              <input
                id="d-by"
                type="text"
                value={signatoryName}
                onChange={(e) => setSignatoryName(e.target.value)}
                required
              />
            </div>
            <SignaturePad ref={sigRef} label="Sign as issuer" />
          </>
        )}

        <div className="btn-stack">
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Generating…' : (supersedeId ? 'Issue new version' : 'Generate & save')}
          </button>
          <button type="button" className="btn btn-ghost" onClick={() => nav('/documents')}>
            Cancel
          </button>
        </div>
      </form>
    </AppShell>
  );
}
